import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  TablePagination
} from '@mui/material';
import { List, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { styled } from '@mui/material/styles';

export default function SpokiLogsPage() {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1 });
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    phone: '',
    type: '',
    status: '',
    date_from: '',
    date_to: ''
  });
  const [selectedLog, setSelectedLog] = useState(null);

  const loadLogs = async (page = 1) => {
    try {
      setLoading(true);
      const params = { page, limit: rowsPerPage };
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      const res = await api.get('/spoki/logs', { params });
      const data = res.data || {};
      setLogs(data.data || []);
      setMeta(data.meta || { page: 1, totalPages: 1 });
    } catch (error) {
      toast.error('Errore nel caricamento dei log Spoki');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs(1);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      loadLogs(1);
    }, 300);
    return () => clearTimeout(t);
  }, [filters, rowsPerPage]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChangePage = (event, newPage) => {
    loadLogs(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    loadLogs(1);
  };

  const handleOpenDetails = (log) => {
    setSelectedLog(log);
  };

  const handleCloseDetails = () => {
    setSelectedLog(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <List size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Log invii Spoki
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitoraggio richieste, risposte ed esiti verso Spoki
            </Typography>
          </Box>
        </Box>
      </Box>
      <StyledCard>
        <CardContent>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              value={filters.phone}
              onChange={(e) => handleFilterChange('phone', e.target.value)}
              placeholder="Cerca per telefono..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={20} />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 300 }}
            />
            <TextField
              label="Tipo"
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              size="small"
              placeholder="template, text, automation"
              sx={{ minWidth: 160 }}
            />
            <TextField
              label="Stato"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              size="small"
              placeholder="sent, error"
              sx={{ minWidth: 140 }}
            />
            <TextField
              label="Da data"
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="A data"
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell>Data</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Direzione</TableCell>
                <TableCell>Telefono</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Stato</TableCell>
                <TableCell>HTTP</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow
                  key={log.id}
                  hover
                  onClick={() => handleOpenDetails(log)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    {log.created_at
                      ? new Date(log.created_at).toLocaleString('it-IT')
                      : '-'}
                  </TableCell>
                  <TableCell>{log.type}</TableCell>
                  <TableCell>{log.direction || '-'}</TableCell>
                  <TableCell>{log.phone || '-'}</TableCell>
                  <TableCell>{log.spoki_template_id || '-'}</TableCell>
                  <TableCell>
                    {log.status ? (
                      <Chip
                        size="small"
                        color={log.status === 'sent' ? 'success' : 'error'}
                        label={log.status}
                      />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{log.http_status || '-'}</TableCell>
                </TableRow>
              ))}
              {logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">
                      Nessun log trovato
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={meta.totalPages * rowsPerPage}
            rowsPerPage={rowsPerPage}
            page={meta.page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Righe per pagina:"
          />
        </CardContent>
      </StyledCard>
      <Dialog open={!!selectedLog} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle>Dettaglio log</DialogTitle>
        <DialogContent dividers>
          {selectedLog && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Request
                </Typography>
                <Box
                  sx={{
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    p: 1.5,
                    fontFamily: 'monospace',
                    fontSize: 12,
                    maxHeight: 260,
                    overflow: 'auto'
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(selectedLog.request_body || {}, null, 2)}
                  </pre>
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Response
                </Typography>
                <Box
                  sx={{
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    p: 1.5,
                    fontFamily: 'monospace',
                    fontSize: 12,
                    maxHeight: 260,
                    overflow: 'auto'
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(selectedLog.response_body || {}, null, 2)}
                  </pre>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));
